import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { api, formatApiError } from '../App';
import {
  MapPin, Upload, Plus, Search, FileText, Download, Trash2,
  Eye, Edit2, ChevronDown, ChevronRight, X, Loader2, File,
  Filter, BarChart3, AlertCircle, CheckCircle, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  sold: 'bg-gray-100 text-gray-600 border-gray-200',
  disputed: 'bg-amber-100 text-amber-700 border-amber-200',
  unknown: 'bg-purple-100 text-purple-600 border-purple-200',
  project: 'bg-blue-100 text-blue-700 border-blue-200',
};

const STATUS_ICONS = {
  active: CheckCircle,
  sold: X,
  disputed: AlertCircle,
  unknown: HelpCircle,
  project: BarChart3,
};

// ==================== STATS CARDS ====================
const StatsCards = ({ stats }) => {
  const cards = [
    { label: 'Total Properties', value: stats.total || 0, color: 'text-[#1a2e44]' },
    { label: 'Active', value: stats.by_status?.active || 0, color: 'text-emerald-600' },
    { label: 'Sold', value: stats.by_status?.sold || 0, color: 'text-gray-500' },
    { label: 'Disputed', value: stats.by_status?.disputed || 0, color: 'text-amber-600' },
    { label: 'Projects', value: stats.by_status?.project || 0, color: 'text-blue-600' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="property-stats">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-lg border border-gray-100 px-4 py-3 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">{c.label}</p>
          <p className={`text-xl font-semibold ${c.color}`}>{c.value}</p>
        </div>
      ))}
    </div>
  );
};

// ==================== PROPERTY DETAIL DRAWER ====================
const PropertyDetail = ({ property, onClose, onUpload, onDeleteDoc, onUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const fileInputRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    if (!property?.id) return;
    try {
      const res = await api.get(`/properties/${property.id}/documents`);
      setDocuments(res.data.documents || []);
    } catch { /* ignore */ }
  }, [property?.id]);

  useEffect(() => {
    loadDocuments();
    if (property) {
      setEditData({
        serial_no: property.serial_no || '',
        area: property.area || '',
        extent: property.extent || '',
        units: property.units || '',
        remarks: property.remarks || '',
        status: property.status || 'active',
        contacts: property.contacts || '',
      });
    }
  }, [property, loadDocuments]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/properties/${property.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded: ${file.name}`);
      loadDocuments();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleViewDoc = async (doc) => {
    try {
      const res = await api.get(`/properties/documents/${doc.id}/download`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_filename}"?`)) return;
    try {
      await api.delete(`/properties/documents/${doc.id}`);
      toast.success('Document deleted');
      loadDocuments();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleSaveEdit = async () => {
    try {
      const payload = { ...editData };
      if (payload.extent) payload.extent = parseFloat(payload.extent) || null;
      await api.put(`/properties/${property.id}`, payload);
      toast.success('Property updated');
      setEditing(false);
      onUpdate?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!property) return null;

  return (
    <Dialog open={!!property} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="property-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <MapPin size={18} className="text-blue-600" />
            {property.serial_no || 'Property'} — {property.area || 'Unknown Area'}
          </DialogTitle>
        </DialogHeader>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-3 text-sm mt-2">
          {editing ? (
            <>
              <div>
                <label className="text-xs text-gray-500 block mb-1">S. No.</label>
                <input className="w-full border rounded px-2 py-1.5 text-sm" value={editData.serial_no}
                  onChange={e => setEditData(d => ({ ...d, serial_no: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Area / Location</label>
                <input className="w-full border rounded px-2 py-1.5 text-sm" value={editData.area}
                  onChange={e => setEditData(d => ({ ...d, area: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Extent</label>
                <input className="w-full border rounded px-2 py-1.5 text-sm" value={editData.extent}
                  onChange={e => setEditData(d => ({ ...d, extent: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Units</label>
                <input className="w-full border rounded px-2 py-1.5 text-sm" value={editData.units}
                  onChange={e => setEditData(d => ({ ...d, units: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Status</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={editData.status}
                  onChange={e => setEditData(d => ({ ...d, status: e.target.value }))}>
                  {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Contacts</label>
                <input className="w-full border rounded px-2 py-1.5 text-sm" value={editData.contacts}
                  onChange={e => setEditData(d => ({ ...d, contacts: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500 block mb-1">Remarks</label>
                <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={3} value={editData.remarks}
                  onChange={e => setEditData(d => ({ ...d, remarks: e.target.value }))} />
              </div>
              <div className="col-span-2 flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="sm" onClick={handleSaveEdit} data-testid="save-property-btn">Save</Button>
              </div>
            </>
          ) : (
            <>
              <InfoRow label="S. No." value={property.serial_no} />
              <InfoRow label="Owner" value={property.owner_name} />
              <InfoRow label="Date" value={property.date} />
              <InfoRow label="RS No." value={property.rs_no} />
              <InfoRow label="Document No." value={property.document_no} />
              <InfoRow label="Area / Location" value={property.area} />
              <InfoRow label="Extent" value={property.extent ? `${property.extent} ${property.units || ''}`.trim() : '—'} />
              <InfoRow label="Status" value={
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${STATUS_COLORS[property.status] || STATUS_COLORS.active}`}>
                  {property.status}
                </span>
              } />
              {property.contacts && <InfoRow label="Contacts" value={property.contacts} className="col-span-2" />}
              {property.remarks && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-0.5">Remarks</p>
                  <p className="text-sm text-gray-800 whitespace-pre-line bg-gray-50 rounded p-2">{property.remarks}</p>
                </div>
              )}
              <div className="col-span-2 flex justify-end">
                <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-property-btn">
                  <Edit2 size={14} className="mr-1" /> Edit
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Sub-parcels */}
        {property.sub_parcels?.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Sub-parcels ({property.sub_parcels.length})</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50"><tr>
                  <th className="px-3 py-2 text-left text-gray-600">RS No.</th>
                  <th className="px-3 py-2 text-left text-gray-600">Doc No.</th>
                  <th className="px-3 py-2 text-right text-gray-600">Extent</th>
                  <th className="px-3 py-2 text-left text-gray-600">Remarks</th>
                </tr></thead>
                <tbody>{property.sub_parcels.map((sp, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-1.5">{sp.rs_no || '—'}</td>
                    <td className="px-3 py-1.5">{sp.document_no || '—'}</td>
                    <td className="px-3 py-1.5 text-right">{sp.extent ?? '—'}</td>
                    <td className="px-3 py-1.5 text-gray-500">{sp.remarks || '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}

        {/* Documents */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">
              <FileText size={14} className="inline mr-1" />
              Documents ({documents.length})
            </h4>
            <div>
              <input type="file" ref={fileInputRef} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff"
                onChange={handleFileUpload} data-testid="doc-file-input" />
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                disabled={uploading} data-testid="upload-doc-btn">
                {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}
                Upload Document
              </Button>
            </div>
          </div>
          {documents.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4 bg-gray-50 rounded">No documents uploaded yet</p>
          ) : (
            <div className="space-y-1.5">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm border border-gray-100">
                  <div className="flex items-center gap-2 min-w-0">
                    <File size={16} className={doc.content_type?.includes('pdf') ? 'text-red-500' : 'text-blue-500'} />
                    <span className="truncate">{doc.original_filename}</span>
                    <span className="text-xs text-gray-400 shrink-0">
                      {(doc.size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleViewDoc(doc)}
                      data-testid={`view-doc-${doc.id}`}>
                      <Eye size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                      onClick={() => handleDeleteDoc(doc)} data-testid={`delete-doc-${doc.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InfoRow = ({ label, value, className = '' }) => (
  <div className={className}>
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <p className="text-sm font-medium text-gray-800">{typeof value === 'string' ? value || '—' : value}</p>
  </div>
);

// ==================== IMPORT DIALOG ====================
const ImportDialog = ({ open, onOpenChange, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/properties/import-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success(res.data.message);
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={18} /> Import Properties from Excel
          </DialogTitle>
          <DialogDescription>
            Upload the VMR Family Properties Excel file. The system will parse the Master and Projects sheets.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <input type="file" ref={fileInputRef} className="hidden" accept=".xlsx,.xls" onChange={handleImport} />
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center cursor-pointer hover:border-blue-300 transition-colors"
            onClick={() => fileInputRef.current?.click()} data-testid="import-dropzone">
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 size={24} className="animate-spin text-blue-500" />
                <p className="text-sm text-gray-500">Processing Excel file...</p>
              </div>
            ) : result ? (
              <div className="flex flex-col items-center gap-2">
                <CheckCircle size={24} className="text-emerald-500" />
                <p className="text-sm font-medium">{result.properties_created} properties imported</p>
                <p className="text-xs text-gray-500">{result.projects_created} project entries created</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload size={24} className="text-gray-400" />
                <p className="text-sm text-gray-600">Click to select Excel file</p>
                <p className="text-xs text-gray-400">Supports .xlsx format</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== MAIN COMPONENT ====================
export default function Properties({ user, setUser }) {
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_owner: {} });
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const fetchAll = useCallback(async () => {
    try {
      const [propsRes, statsRes, ownersRes] = await Promise.all([
        api.get('/properties', {
          params: {
            ...(selectedOwner !== 'all' && { owner: selectedOwner }),
            ...(selectedStatus !== 'all' && { status: selectedStatus }),
            ...(searchQuery && { search: searchQuery }),
          },
        }),
        api.get('/properties/stats'),
        api.get('/properties/owners'),
      ]);
      setProperties(propsRes.data.properties || []);
      setStats(statsRes.data);
      setOwners(ownersRes.data.owners || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [selectedOwner, selectedStatus, searchQuery]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDelete = async (prop) => {
    if (!window.confirm(`Delete property ${prop.serial_no || prop.rs_no}?`)) return;
    try {
      await api.delete(`/properties/${prop.id}`);
      toast.success('Property deleted');
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Group properties by owner for display
  const groupedByOwner = properties.reduce((acc, p) => {
    const key = p.owner_name || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  // Calculate total extent per group
  const groupExtents = {};
  for (const [owner, props] of Object.entries(groupedByOwner)) {
    groupExtents[owner] = props.reduce((sum, p) => {
      const mainExtent = p.extent || 0;
      const subExtent = (p.sub_parcels || []).reduce((s, sp) => s + (sp.extent || 0), 0);
      return sum + mainExtent + subExtent;
    }, 0);
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="properties-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-xl font-semibold text-[#1a2e44]">Master Properties</h1>
            <p className="text-sm text-gray-500 mt-0.5">Family land holdings, parcels & development projects</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="import-excel-btn">
              <Upload size={14} className="mr-1.5" /> Import Excel
            </Button>
          </div>
        </div>

        {/* Stats */}
        <StatsCards stats={stats} />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by S.No, RS No, area, owner..."
              className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              data-testid="property-search"
            />
          </div>
          {/* Owner filter */}
          <select
            className="border rounded-lg px-3 py-2 text-sm min-w-[180px] focus:outline-none focus:ring-1 focus:ring-blue-300"
            value={selectedOwner}
            onChange={e => setSelectedOwner(e.target.value)}
            data-testid="owner-filter"
          >
            <option value="all">All Owners ({stats.total})</option>
            {owners.map(o => (
              <option key={o} value={o}>{o} ({stats.by_owner?.[o] || 0})</option>
            ))}
          </select>
          {/* Status filter */}
          <select
            className="border rounded-lg px-3 py-2 text-sm min-w-[140px] focus:outline-none focus:ring-1 focus:ring-blue-300"
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            data-testid="status-filter"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active ({stats.by_status?.active || 0})</option>
            <option value="sold">Sold ({stats.by_status?.sold || 0})</option>
            <option value="disputed">Disputed ({stats.by_status?.disputed || 0})</option>
            <option value="project">Projects ({stats.by_status?.project || 0})</option>
            <option value="unknown">Unknown ({stats.by_status?.unknown || 0})</option>
          </select>
        </div>

        {/* Properties Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-gray-400" size={28} />
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-lg border border-gray-100">
            <MapPin size={36} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No properties found</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowImport(true)}>
              <Upload size={14} className="mr-1" /> Import from Excel
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden" data-testid="properties-table">
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/80">
                    <th className="w-8 px-2 py-2.5"></th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S. No.</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RS No.</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Extent</th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedByOwner).map(([ownerName, ownerProps]) => (
                    <React.Fragment key={ownerName}>
                      {/* Owner group header — only shown when filter is "all" */}
                      {selectedOwner === 'all' && (
                        <tr className="bg-[#f0f4f8]">
                          <td colSpan={5} className="px-3 py-2 text-xs font-semibold text-[#1a2e44] uppercase tracking-wide">
                            {ownerName}
                          </td>
                          <td className="px-3 py-2 text-xs text-right font-medium text-gray-600">
                            {groupExtents[ownerName]?.toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">{ownerProps.length} records</td>
                          <td colSpan={2}></td>
                        </tr>
                      )}
                      {ownerProps.map(prop => {
                        const hasSubs = prop.sub_parcels?.length > 0;
                        const isExpanded = expandedRows.has(prop.id);
                        const StatusIcon = STATUS_ICONS[prop.status] || CheckCircle;
                        return (
                          <React.Fragment key={prop.id}>
                            <tr className="border-t border-gray-50 hover:bg-blue-50/30 cursor-pointer transition-colors"
                              onClick={() => setSelectedProperty(prop)} data-testid={`property-row-${prop.serial_no || prop.id}`}>
                              <td className="px-2 py-2 text-center">
                                {hasSubs && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleRow(prop.id); }}
                                    className="text-gray-400 hover:text-gray-600 p-0.5"
                                    data-testid={`expand-${prop.id}`}
                                  >
                                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  </button>
                                )}
                              </td>
                              <td className="px-3 py-2 font-medium text-[#1a2e44]">
                                {prop.serial_no || '—'}
                                {prop.project_name && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); if (prop.project_id) window.location.href = `/projects/${prop.project_id}`; }}
                                    className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors"
                                    data-testid={`project-badge-${prop.id}`}
                                    title={`Linked to project: ${prop.project_name}`}
                                  >
                                    {prop.project_name}
                                  </button>
                                )}
                              </td>
                              <td className="px-3 py-2 text-gray-700 font-mono text-xs">{prop.rs_no || '—'}</td>
                              <td className="px-3 py-2 text-gray-700">{prop.area || '—'}</td>
                              <td className="px-3 py-2 text-gray-500 text-xs">{selectedOwner !== 'all' ? '' : prop.owner_name}</td>
                              <td className="px-3 py-2 text-right font-medium">
                                {prop.extent != null ? prop.extent : '—'}
                                {prop.units && <span className="text-xs text-gray-400 ml-1">{prop.units}</span>}
                              </td>
                              <td className="px-3 py-2 text-gray-500 text-xs">{prop.date || '—'}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${STATUS_COLORS[prop.status] || STATUS_COLORS.active}`}>
                                  <StatusIcon size={10} />
                                  {prop.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button onClick={(e) => { e.stopPropagation(); setSelectedProperty(prop); }}
                                    className="p-1 text-gray-400 hover:text-blue-500 rounded" data-testid={`view-${prop.id}`}>
                                    <Eye size={14} />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); handleDelete(prop); }}
                                    className="p-1 text-gray-400 hover:text-red-500 rounded" data-testid={`delete-${prop.id}`}>
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {/* Expanded sub-parcels */}
                            {hasSubs && isExpanded && prop.sub_parcels.map((sp, i) => (
                              <tr key={`${prop.id}-sub-${i}`} className="bg-gray-50/50 border-t border-gray-50">
                                <td></td>
                                <td className="px-3 py-1.5 text-xs text-gray-400 pl-8">sub</td>
                                <td className="px-3 py-1.5 text-xs font-mono text-gray-600">{sp.rs_no || '—'}</td>
                                <td className="px-3 py-1.5 text-xs text-gray-500">{sp.remarks || '—'}</td>
                                <td></td>
                                <td className="px-3 py-1.5 text-xs text-right text-gray-600">
                                  {sp.extent ?? '—'} {sp.units && <span className="text-gray-400">{sp.units}</span>}
                                </td>
                                <td colSpan={3} className="px-3 py-1.5 text-xs text-gray-400">{sp.document_no || ''}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Property Detail Drawer */}
        <PropertyDetail
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
          onUpdate={fetchAll}
        />

        {/* Import Dialog */}
        <ImportDialog
          open={showImport}
          onOpenChange={setShowImport}
          onSuccess={fetchAll}
        />
      </div>
    </Layout>
  );
}
